// src/pages/finance-portal/statements/Statements.js

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Box, Typography, Paper, Button, TextField, Card,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    TablePagination, Chip, IconButton, Alert, Skeleton, Tooltip,
    InputAdornment, Autocomplete, CircularProgress,
    Dialog, DialogTitle, DialogContent, DialogActions,
    FormControl, InputLabel, Select, MenuItem,
} from '@mui/material';
import SummarizeIcon from '@mui/icons-material/Summarize';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SendIcon from '@mui/icons-material/Send';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../../../utils/axiosInstance';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#22c55e';

const TYPE_COLORS = {
    OPEN_ITEM: { bg: '#dbeafe', color: '#1e40af' },
    BALANCE_FORWARD: { bg: '#ede9fe', color: '#6d28d9' },
};

function formatCurrency(val) {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val || 0);
}

function formatDate(dateStr) {
    if (!dateStr) return '\u2014';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function getTypeLabel(type) {
    if (!type) return '';
    return type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

export default function Statements() {
    const navigate = useNavigate();
    const [statements, setStatements] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(20);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [search, setSearch] = useState('');

    // Generate Statement dialog
    const [generateOpen, setGenerateOpen] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [genCustomerOptions, setGenCustomerOptions] = useState([]);
    const [genCustomerSearch, setGenCustomerSearch] = useState('');
    const [genSelectedCustomer, setGenSelectedCustomer] = useState(null);
    const [genCustomerLoading, setGenCustomerLoading] = useState(false);
    const genCustomerTimer = useRef(null);
    const [genPeriodStart, setGenPeriodStart] = useState('');
    const [genPeriodEnd, setGenPeriodEnd] = useState('');
    const [genStatementType, setGenStatementType] = useState('OPEN_ITEM');

    // Bulk Generate dialog
    const [bulkOpen, setBulkOpen] = useState(false);
    const [bulkGenerating, setBulkGenerating] = useState(false);
    const [bulkPeriodStart, setBulkPeriodStart] = useState('');
    const [bulkPeriodEnd, setBulkPeriodEnd] = useState('');

    // Send dialog
    const [sendDialog, setSendDialog] = useState({ open: false, statement: null });
    const [sending, setSending] = useState(false);

    const fetchStatements = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const params = { page: page + 1, per_page: rowsPerPage };
            if (search) params.search = search;
            const res = await axiosInstance.get('/api/finance/statements', { params });
            if (res.data.success) {
                setStatements(res.data.data);
                setTotal(res.data.total);
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load statements');
        } finally {
            setIsLoading(false);
        }
    }, [page, rowsPerPage, search]);

    useEffect(() => { fetchStatements(); }, [fetchStatements]);

    // Customer search for generate dialog
    useEffect(() => {
        if (genCustomerTimer.current) clearTimeout(genCustomerTimer.current);
        const params = { per_page: 50 };
        if (genCustomerSearch && genCustomerSearch.length >= 2) params.search = genCustomerSearch;
        setGenCustomerLoading(true);
        genCustomerTimer.current = setTimeout(async () => {
            try {
                const res = await axiosInstance.get('/api/finance/customers', { params });
                if (res.data.success) {
                    setGenCustomerOptions(res.data.data || []);
                }
            } catch (err) {
                console.error('Failed to search customers', err);
            } finally {
                setGenCustomerLoading(false);
            }
        }, 300);
        return () => { if (genCustomerTimer.current) clearTimeout(genCustomerTimer.current); };
    }, [genCustomerSearch]);

    const handleGenerate = async () => {
        if (!genSelectedCustomer || !genPeriodStart || !genPeriodEnd) return;
        setGenerating(true);
        setError(null);
        try {
            await axiosInstance.post('/api/finance/statements/generate', {
                customer_id: genSelectedCustomer.id,
                period_start: genPeriodStart,
                period_end: genPeriodEnd,
                statement_type: genStatementType,
            });
            setGenerateOpen(false);
            setGenSelectedCustomer(null);
            setGenPeriodStart('');
            setGenPeriodEnd('');
            setGenStatementType('OPEN_ITEM');
            fetchStatements();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to generate statement');
        } finally {
            setGenerating(false);
        }
    };

    const handleBulkGenerate = async () => {
        if (!bulkPeriodStart || !bulkPeriodEnd) return;
        setBulkGenerating(true);
        setError(null);
        try {
            await axiosInstance.post('/api/finance/statements/bulk-generate', {
                period_start: bulkPeriodStart,
                period_end: bulkPeriodEnd,
            });
            setBulkOpen(false);
            setBulkPeriodStart('');
            setBulkPeriodEnd('');
            fetchStatements();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to bulk generate statements');
        } finally {
            setBulkGenerating(false);
        }
    };

    const handleSend = async () => {
        setSending(true);
        setError(null);
        try {
            await axiosInstance.post(`/api/finance/statements/${sendDialog.statement.id}/send`, {});
            setSendDialog({ open: false, statement: null });
            fetchStatements();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to send statement');
        } finally {
            setSending(false);
        }
    };

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
            {/* Header */}
            <Paper elevation={0} sx={{
                background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                borderRadius: { xs: 0, md: '0 0 24px 24px' },
                px: { xs: 2.5, md: 4 }, py: { xs: 3, md: 4 }, mb: 3,
                position: 'relative', overflow: 'hidden',
            }}>
                <Box sx={{ position: 'absolute', top: -30, right: -30, width: 160, height: 160, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.06)' }} />
                <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{ p: 1.5, borderRadius: '14px', bgcolor: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <SummarizeIcon sx={{ fontSize: 28, color: '#fff' }} />
                        </Box>
                        <Box>
                            <Typography variant="h4" sx={{ fontFamily, fontWeight: 800, color: 'white', fontSize: { xs: '1.4rem', md: '1.85rem' } }}>Statements</Typography>
                            <Typography sx={{ fontFamily, color: 'rgba(255,255,255,0.8)', fontSize: '0.9rem', mt: 0.25 }}>Generate and manage customer statements</Typography>
                        </Box>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1.5 }}>
                        <Button
                            variant="contained"
                            startIcon={<PlaylistAddIcon />}
                            onClick={() => setBulkOpen(true)}
                            sx={{
                                fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px',
                                bgcolor: 'rgba(255,255,255,0.2)', color: 'white',
                                '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
                            }}
                        >
                            Bulk Generate
                        </Button>
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={() => setGenerateOpen(true)}
                            sx={{
                                fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px',
                                bgcolor: 'white', color: ACCENT,
                                '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' },
                            }}
                        >
                            Generate Statement
                        </Button>
                    </Box>
                </Box>
            </Paper>

            <Box sx={{ px: { xs: 2, md: 4 }, pb: 4, maxWidth: 1400, mx: 'auto' }}>
                {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2, borderRadius: '12px', fontFamily }}>{error}</Alert>}

                {/* Search */}
                <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                    <TextField
                        size="small"
                        placeholder="Search statements..."
                        value={search}
                        onChange={e => { setSearch(e.target.value); setPage(0); }}
                        InputProps={{
                            startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: '#94a3b8' }} /></InputAdornment>,
                            sx: { fontFamily, borderRadius: '10px' },
                        }}
                        sx={{ minWidth: 280 }}
                    />
                </Box>

                {/* Table */}
                <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                    {['Statement #', 'Customer', 'Type', 'Period', 'Opening Balance', 'Closing Balance', 'Sent', 'Actions'].map(h => (
                                        <TableCell key={h} sx={{ fontFamily, fontWeight: 700, fontSize: '0.75rem', color: '#64748b', textTransform: 'uppercase', borderBottom: '2px solid #e2e8f0' }}>{h}</TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {isLoading ? (
                                    [1, 2, 3, 4, 5].map(i => (
                                        <TableRow key={i}>
                                            {[1, 2, 3, 4, 5, 6, 7, 8].map(j => (
                                                <TableCell key={j}><Skeleton height={36} /></TableCell>
                                            ))}
                                        </TableRow>
                                    ))
                                ) : statements.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={8} sx={{ textAlign: 'center', py: 6 }}>
                                            <SummarizeIcon sx={{ fontSize: 48, color: '#cbd5e1', mb: 1 }} />
                                            <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.95rem' }}>No statements found</Typography>
                                            <Button
                                                size="small"
                                                startIcon={<AddIcon />}
                                                onClick={() => setGenerateOpen(true)}
                                                sx={{ fontFamily, textTransform: 'none', color: ACCENT, mt: 1 }}
                                            >
                                                Generate your first statement
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ) : statements.map(stmt => {
                                    const typeStyle = TYPE_COLORS[stmt.statement_type] || {};
                                    const customerName = stmt.customer_name || stmt.customer_company || '\u2014';
                                    return (
                                        <TableRow
                                            key={stmt.id}
                                            hover
                                            sx={{ cursor: 'pointer', '&:hover': { bgcolor: '#f8fafc' } }}
                                            onClick={() => navigate(`/finance-portal/statements/${stmt.id}`)}
                                        >
                                            <TableCell sx={{ fontFamily, fontWeight: 600, fontSize: '0.85rem', color: '#1e293b' }}>
                                                {stmt.statement_number || '\u2014'}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, fontSize: '0.85rem', color: '#475569', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                {customerName}
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={getTypeLabel(stmt.statement_type)}
                                                    size="small"
                                                    sx={{
                                                        fontFamily, fontSize: '0.7rem', fontWeight: 600, height: 22,
                                                        bgcolor: typeStyle.bg, color: typeStyle.color,
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, fontSize: '0.85rem', color: '#475569' }}>
                                                {formatDate(stmt.period_start)} \u2013 {formatDate(stmt.period_end)}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, fontSize: '0.85rem', fontWeight: 600, color: '#1e293b' }}>
                                                {formatCurrency(stmt.opening_balance)}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, fontSize: '0.85rem', fontWeight: 600, color: '#1e293b' }}>
                                                {formatCurrency(stmt.closing_balance)}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, fontSize: '0.85rem', color: '#475569' }}>
                                                {stmt.sent_at ? (
                                                    <Chip label={formatDate(stmt.sent_at)} size="small" sx={{ fontFamily, fontSize: '0.7rem', fontWeight: 600, height: 22, bgcolor: '#dcfce7', color: '#166534' }} />
                                                ) : (
                                                    <Chip label="Not Sent" size="small" sx={{ fontFamily, fontSize: '0.7rem', fontWeight: 600, height: 22, bgcolor: '#f3f4f6', color: '#6b7280' }} />
                                                )}
                                            </TableCell>
                                            <TableCell onClick={e => e.stopPropagation()}>
                                                <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                    <Tooltip title="View">
                                                        <IconButton size="small" onClick={() => navigate(`/finance-portal/statements/${stmt.id}`)} sx={{ color: '#94a3b8' }}>
                                                            <VisibilityIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                    {!stmt.sent_at && (
                                                        <Tooltip title="Send">
                                                            <IconButton size="small" onClick={() => setSendDialog({ open: true, statement: stmt })} sx={{ color: '#94a3b8', '&:hover': { color: '#3b82f6' } }}>
                                                                <SendIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        component="div"
                        count={total}
                        page={page}
                        onPageChange={(_, p) => setPage(p)}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                        sx={{ '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': { fontFamily } }}
                    />
                </Card>
            </Box>

            {/* Generate Statement Dialog */}
            <Dialog open={generateOpen} onClose={() => setGenerateOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '16px', p: 1 } }}>
                <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>Generate Statement</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mt: 1 }}>
                        <Autocomplete
                            size="small"
                            options={genCustomerOptions}
                            getOptionLabel={opt => opt ? (opt.company_name || `${opt.first_name || ''} ${opt.last_name || ''}`.trim()) : ''}
                            value={genSelectedCustomer}
                            onChange={(_, val) => setGenSelectedCustomer(val)}
                            onInputChange={(_, val) => setGenCustomerSearch(val)}
                            loading={genCustomerLoading}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Customer"
                                    required
                                    InputLabelProps={{ ...params.InputLabelProps, sx: { fontFamily } }}
                                    InputProps={{
                                        ...params.InputProps,
                                        sx: { fontFamily, borderRadius: '10px' },
                                        endAdornment: (
                                            <>
                                                {genCustomerLoading ? <CircularProgress color="inherit" size={16} /> : null}
                                                {params.InputProps.endAdornment}
                                            </>
                                        ),
                                    }}
                                />
                            )}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            noOptionsText="Type to search customers"
                        />
                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                            <TextField
                                size="small" type="date" label="Period Start" required
                                value={genPeriodStart}
                                onChange={e => setGenPeriodStart(e.target.value)}
                                InputLabelProps={{ shrink: true, sx: { fontFamily } }}
                                InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                            />
                            <TextField
                                size="small" type="date" label="Period End" required
                                value={genPeriodEnd}
                                onChange={e => setGenPeriodEnd(e.target.value)}
                                InputLabelProps={{ shrink: true, sx: { fontFamily } }}
                                InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                            />
                        </Box>
                        <FormControl size="small">
                            <InputLabel sx={{ fontFamily }}>Statement Type</InputLabel>
                            <Select
                                value={genStatementType}
                                onChange={e => setGenStatementType(e.target.value)}
                                label="Statement Type"
                                sx={{ fontFamily, borderRadius: '10px' }}
                            >
                                <MenuItem value="OPEN_ITEM" sx={{ fontFamily }}>Open Item</MenuItem>
                                <MenuItem value="BALANCE_FORWARD" sx={{ fontFamily }}>Balance Forward</MenuItem>
                            </Select>
                        </FormControl>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setGenerateOpen(false)} sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleGenerate}
                        disabled={generating || !genSelectedCustomer || !genPeriodStart || !genPeriodEnd}
                        startIcon={generating ? <CircularProgress size={16} color="inherit" /> : <AddIcon />}
                        sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px', bgcolor: ACCENT, '&:hover': { bgcolor: '#16a34a' } }}
                    >
                        {generating ? 'Generating...' : 'Generate'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Bulk Generate Dialog */}
            <Dialog open={bulkOpen} onClose={() => setBulkOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '16px', p: 1 } }}>
                <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>Bulk Generate Statements</DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontFamily, color: '#64748b', fontSize: '0.9rem', mb: 2 }}>
                        Generate statements for all customers with an outstanding balance during the selected period.
                    </Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mt: 1 }}>
                        <TextField
                            size="small" type="date" label="Period Start" required
                            value={bulkPeriodStart}
                            onChange={e => setBulkPeriodStart(e.target.value)}
                            InputLabelProps={{ shrink: true, sx: { fontFamily } }}
                            InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                        />
                        <TextField
                            size="small" type="date" label="Period End" required
                            value={bulkPeriodEnd}
                            onChange={e => setBulkPeriodEnd(e.target.value)}
                            InputLabelProps={{ shrink: true, sx: { fontFamily } }}
                            InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                        />
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setBulkOpen(false)} sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleBulkGenerate}
                        disabled={bulkGenerating || !bulkPeriodStart || !bulkPeriodEnd}
                        startIcon={bulkGenerating ? <CircularProgress size={16} color="inherit" /> : <PlaylistAddIcon />}
                        sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px', bgcolor: ACCENT, '&:hover': { bgcolor: '#16a34a' } }}
                    >
                        {bulkGenerating ? 'Generating...' : 'Bulk Generate'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Send Confirmation Dialog */}
            <Dialog open={sendDialog.open} onClose={() => setSendDialog({ open: false, statement: null })} PaperProps={{ sx: { borderRadius: '16px', p: 1 } }}>
                <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>Send Statement {sendDialog.statement?.statement_number}?</DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontFamily, color: '#64748b' }}>
                        This will send the statement to the customer via email.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setSendDialog({ open: false, statement: null })} sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleSend}
                        disabled={sending}
                        startIcon={sending ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
                        sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px', bgcolor: '#3b82f6', '&:hover': { bgcolor: '#2563eb' } }}
                    >
                        {sending ? 'Sending...' : 'Send'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
